import * as z from "zod";

const CONFIRMATION_DATA_INPUT_KEY = "__askRedoConfirmationData";
const CONFIRMATION_PROMPT_INPUT_KEY = "__askRedoConfirmationPrompt";

export interface AskRedoConfirmationData {
  confirmationData: unknown;
  confirmationPrompt?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function withAskRedoConfirmationData(
  input: unknown,
  confirmationData: unknown,
  confirmationPrompt: string | undefined,
): unknown {
  if (confirmationData === undefined && confirmationPrompt === undefined) {
    return input;
  }

  const enriched: Record<string, unknown> = isRecord(input)
    ? { ...input }
    : { value: input };

  if (confirmationData !== undefined) {
    enriched[CONFIRMATION_DATA_INPUT_KEY] = confirmationData;
  }
  if (confirmationPrompt !== undefined) {
    enriched[CONFIRMATION_PROMPT_INPUT_KEY] = confirmationPrompt;
  }

  return enriched;
}

export function readAskRedoConfirmationData(
  input: unknown,
): AskRedoConfirmationData | undefined {
  // A confirmation may carry only a prompt (ConfirmToolCallError with no
  // data payload) — that alone is worth surfacing on the card.
  if (
    !isRecord(input) ||
    !(
      CONFIRMATION_DATA_INPUT_KEY in input ||
      CONFIRMATION_PROMPT_INPUT_KEY in input
    )
  ) {
    return undefined;
  }

  const prompt = input[CONFIRMATION_PROMPT_INPUT_KEY];
  return {
    confirmationData: input[CONFIRMATION_DATA_INPUT_KEY],
    confirmationPrompt: typeof prompt === "string" ? prompt : undefined,
  };
}

const INVENTORY_TOOLSET_ID = "inventory";

const rpcWriteProposalSchema = z.object({
  name: z.string(),
  input: z.unknown(),
});

/** A staged RPC mutation: the RPC name plus the input it would run with. */
export type RpcWriteProposal = z.infer<typeof rpcWriteProposalSchema>;

/**
 * The payload `execute_code` stages for confirmation. Per-toolset write arrays
 * stay `unknown` here because each toolset owns its own write shape — inventory
 * entries are parsed by ChangelogCard, RPC writes by {@link rpcWriteProposalSchema}.
 */
const executeCodeConfirmationSchema = z.object({
  writesByToolset: z.record(z.string(), z.array(z.unknown())),
  summary: z.string().optional(),
});

/** An `execute_code` confirmation split by how each toolset's writes render. */
export interface ExecuteCodeWrites {
  /**
   * Inventory changelog entries, rendered by the rich ChangelogCard, which owns
   * (and parses) their shape — hence `unknown` here. Inventory predates the
   * generic write system and keeps its bespoke card; new surfaces go through
   * `rpcWrites` instead of adding more per-surface fields here.
   */
  inventoryChangelog: unknown[] | undefined;
  /** Writes from every non-inventory toolset, rendered generically (the
   *  render layer adapts manual-order writes into the rich ChangelogCard). */
  rpcWrites: RpcWriteProposal[];
  /** Summary covering every staged write. */
  summary: string | undefined;
  confirmationPrompt: string | undefined;
}

/**
 * Split an `execute_code` approval into inventory changelog writes (rich card)
 * and generic RPC writes (name + input), keyed by toolset id. One call stages
 * writes from one or more toolsets but yields a single approval. Returns
 * undefined (caller falls back to the generic copy) if the payload is malformed.
 */
export function readExecuteCodeWrites(
  input: unknown,
): ExecuteCodeWrites | undefined {
  const confirmation = readAskRedoConfirmationData(input);
  if (!confirmation) {
    return undefined;
  }
  const parsed = executeCodeConfirmationSchema.safeParse(
    confirmation.confirmationData,
  );
  if (!parsed.success) {
    return undefined;
  }

  const { writesByToolset, summary } = parsed.data;
  const rpcWrites = z.array(rpcWriteProposalSchema).safeParse(
    Object.entries(writesByToolset)
      .filter(([id]) => id !== INVENTORY_TOOLSET_ID)
      .flatMap(([, writes]) => writes),
  );
  if (!rpcWrites.success) {
    return undefined;
  }

  return {
    inventoryChangelog: writesByToolset[INVENTORY_TOOLSET_ID],
    rpcWrites: rpcWrites.data,
    summary,
    confirmationPrompt: confirmation.confirmationPrompt,
  };
}
